import {
    clickAddAddress,
    clickAddressChangeBack,
    clickChangeAddress,
    clickSelectAddress,
} from "@cart-client/analytics/events";
import {
    clickCartAddressNudgeToolTipClose,
    impressionAddressNudgeTooltip,
    impressionBottomAddressList,
    impressionCheckoutLatLng,
    clickReloadAddresses,
    clickAddOrSelectAddress,
    impressionAddOrSelectAddress,
    impressionBottomAddressListError,
} from "@cart-client/analytics/events/CartAddressNudge";
import { CartAddressContext } from "@cart-client/contexts/CartAddressContext";
import { CartContext } from "@cart-client/contexts/CartContext";
import { CartHeaderContext } from "@cart-client/contexts/CartHeaderContext";
import { getDistanceLabel } from "@cart-client/helpers/CartAddress";
import { getSelectedAddress } from "@cart-client/helpers/CartFooter";
import { AddressInfo } from "@swiggy-private/cart-types";
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { format } from "@swiggy-private/core-ui";
import { useCallback, useContext, useMemo } from "react";

export const useCartAddressNudgeAnalytics = () => {
    const { impressionEvent, clickEvent } = useContext(AnalyticsContext);
    const { cartAddressState } = useContext(CartAddressContext);
    const {
        cartHeaderState: { isBlack },
    } = useContext(CartHeaderContext);
    const {
        cartState: { reviewedCart: cartResponse },
    } = useContext(CartContext);

    const {
        addressNudgeConfig: {
            currentLocation,
            haversineDistance,
            cart_address_nudge_message: nudgeMessage,
            isAddressNudgeXpEnabled,
        },
    } = cartAddressState;
    const selectedAddress = useMemo(() => getSelectedAddress(cartResponse?.data?.addresses), [
        cartResponse?.data?.addresses,
    ]);

    const selectedAddressContext = useMemo(
        () => ({
            selectedAdd: {
                id: selectedAddress?.id || "",
                lat: selectedAddress?.lat || "",
                lng: selectedAddress?.lng || "",
            },
            gpsAddDist: haversineDistance || "",
        }),
        [haversineDistance, selectedAddress],
    );
    const currentLocationAndFlagsContext = useMemo(
        () => ({
            gpsLat: currentLocation?.lat || "",
            gpsLng: currentLocation?.lng || "",
            enabledFlag: !!isAddressNudgeXpEnabled,
            isBlackFlag: isBlack,
        }),
        [currentLocation, isAddressNudgeXpEnabled, isBlack],
    );
    const nudgeText = useMemo(
        () => (nudgeMessage && haversineDistance ? format(nudgeMessage, getDistanceLabel(haversineDistance)) : ""),
        [haversineDistance, nudgeMessage],
    );
    const cartIDs = useMemo(
        () =>
            JSON.stringify({
                cartId: cartResponse?.data?.cartId || "",
                originalCartId: cartResponse?.data?.originalCartId || "",
            }),
        [cartResponse?.data],
    );

    const onImpressionAddressNudgeTooltip = useCallback(() => {
        impressionAddressNudgeTooltip(impressionEvent, {
            addressNudgeContext: JSON.stringify({ ...selectedAddressContext, ...currentLocationAndFlagsContext }),
            nudgeText,
            cartIDs,
        });
    }, [cartIDs, currentLocationAndFlagsContext, impressionEvent, nudgeText, selectedAddressContext]);

    const onImpressionCheckoutLatLng = useCallback(() => {
        impressionCheckoutLatLng(impressionEvent, {
            addressNudgeContext: JSON.stringify({ ...selectedAddressContext, ...currentLocationAndFlagsContext }),
            cartIDs,
        });
    }, [cartIDs, currentLocationAndFlagsContext, impressionEvent, selectedAddressContext]);

    const onClickAddressNudgeToolTipClose = useCallback(() => {
        clickCartAddressNudgeToolTipClose(clickEvent, {
            addressNudgeContext: JSON.stringify({ ...selectedAddressContext, ...currentLocationAndFlagsContext }),
            nudgeText,
            cartIDs,
        });
    }, [cartIDs, clickEvent, currentLocationAndFlagsContext, nudgeText, selectedAddressContext]);

    const onImpressionBottomAddressList = useCallback(
        (selectedAddress: AddressInfo | null, addressList: AddressInfo[]) => {
            impressionBottomAddressList(impressionEvent, {
                addressesAndNudgeContext: JSON.stringify({
                    selectedAddress,
                    addressList,
                    ...currentLocationAndFlagsContext,
                }),
                nudgeText,
                cartIDs,
            });
        },
        [cartIDs, currentLocationAndFlagsContext, impressionEvent, nudgeText],
    );

    const onClickSelectAddress = useCallback(
        (selectedAddress: AddressInfo, addressList: AddressInfo[]) => {
            const addressesAndNudgeContext = JSON.stringify({
                selectedAddress,
                addressList,
                ...currentLocationAndFlagsContext,
            });

            clickSelectAddress(clickEvent, { nudgeText, addressesAndNudgeContext, cartIDs });
        },
        [cartIDs, clickEvent, currentLocationAndFlagsContext, nudgeText],
    );

    const onClickChangeAddress = useCallback(
        (isCartStatusSuccess?: boolean, cartTotal?: number) => {
            const cartStatusAndAddressesContext = JSON.stringify({
                isUnserviceableCart: !isCartStatusSuccess || selectedAddress?.delivery_valid !== 1,
                selectedAdd: selectedAddressContext.selectedAdd,
                ...currentLocationAndFlagsContext,
            });
            cartTotal &&
                clickChangeAddress(
                    {
                        cartAmount: cartTotal,
                        cartStatusAndAddressesContext,
                        cartIDs,
                    },
                    clickEvent,
                );
        },
        [
            cartIDs,
            clickEvent,
            currentLocationAndFlagsContext,
            selectedAddress?.delivery_valid,
            selectedAddressContext.selectedAdd,
        ],
    );

    const onClickAddressChangeBack = useCallback(() => {
        clickAddressChangeBack(clickEvent, cartIDs);
    }, [cartIDs, clickEvent]);

    const onClickAddNewAddress = useCallback(
        (cartTotal?: number, selectedAddress?: AddressInfo | null, addressList?: AddressInfo[], source?: string) => {
            const addressesAndNudgeContext = JSON.stringify({
                selectedAddress,
                addressList,
                source,
                ...currentLocationAndFlagsContext,
            });
            cartTotal &&
                clickAddAddress(
                    {
                        cartTotal,
                        addressesAndNudgeContext,
                        nudgeText,
                        cartIDs,
                    },
                    clickEvent,
                );
        },
        [cartIDs, clickEvent, currentLocationAndFlagsContext, nudgeText],
    );

    const onClickReloadAddresses = useCallback(() => {
        clickReloadAddresses(clickEvent, cartIDs);
    }, [cartIDs, clickEvent]);

    const onClickAddOrSelectAddress = useCallback(() => {
        clickAddOrSelectAddress(clickEvent, cartIDs);
    }, [cartIDs, clickEvent]);

    const onImpressionAddOrSelectAddress = useCallback(() => {
        impressionAddOrSelectAddress(impressionEvent, cartIDs);
    }, [cartIDs, impressionEvent]);

    const onImpressionBottomAddressListError = useCallback(() => {
        impressionBottomAddressListError(impressionEvent, cartIDs);
    }, [cartIDs, impressionEvent]);

    return {
        onImpressionAddressNudgeTooltip,
        onImpressionCheckoutLatLng,
        onClickAddressNudgeToolTipClose,
        onImpressionBottomAddressList,
        onClickSelectAddress,
        onClickChangeAddress,
        onClickAddressChangeBack,
        onClickAddNewAddress,
        onClickReloadAddresses,
        onClickAddOrSelectAddress,
        onImpressionAddOrSelectAddress,
        onImpressionBottomAddressListError,
    };
};
